import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { selectAppReady, selectCurrentMasterSettings } from '@lib/selectors';
import axiosClient from '@lib/utils/axiosClient';

import { TBrandingSettingsTheme, TCustomWebStylesModelV2 } from './types';

interface ReturnData {
  response: TCustomWebStylesModelV2[];
}
const queryFn = () =>
  axiosClient
    .get<ReturnData>(`v2/customWebStyles?limit=3&_sort=createdAt:desc`)
    .then(({ data }) => data?.response?.reverse());

const useEmbedor = () => {
  const isAppSelectorReady = useSelector(selectAppReady);
  const currentMasterSettings = useSelector(selectCurrentMasterSettings);

  const appIsReady = isAppSelectorReady && currentMasterSettings;

  const hasEmbedEnabled =
    !!appIsReady &&
    !!currentMasterSettings?.branding?.themes?.some(
      (theme: TBrandingSettingsTheme) => theme.scope?.includes('embeds') && theme.enabled,
    );

  // Leave false until white labeling builder is ready
  const { data = [], isLoading, isSuccess, isError, isIdle } = useQuery<TCustomWebStylesModelV2[]>(
    'custom-web-styles',
    queryFn,
    {
      cacheTime: 0,
      enabled: hasEmbedEnabled,
    },
  );

  const publishedEmbedCustomStyles = data.find(
    (customStyle) => customStyle.published && customStyle.scope === 'embeds',
  );
  const embedTheme = publishedEmbedCustomStyles?.styles.theme;
  const embedFontFamilyCDN = embedTheme?.fontFamily?.cdn;

  const values = useMemo(
    () => ({ isLoading, isSuccess, embedFontFamilyCDN, embedTheme, appIsReady, hasEmbedEnabled, isIdle, isError }),
    [appIsReady, embedFontFamilyCDN, embedTheme, isLoading, isError, isSuccess, hasEmbedEnabled, isIdle],
  );
  return values;
};

export default useEmbedor;
